import React, { useEffect, useRef, useState, useMemo } from 'react';
import {
    Autocomplete,
    Box,
    Button,
    Grid,
    IconButton,
    TextField,
    Typography,
    Chip,
    Card,
    CardContent,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Stack,
    InputAdornment,
    CircularProgress
} from '@mui/material';
import {
    ArrowBack as ArrowBackIcon,
    Add as AddIcon,
    Delete as DeleteIcon,
    Send as SendIcon,
    ExpandMore as ExpandMoreIcon,
    Draw as DrawIcon,
    Clear as ClearIcon,
    PersonSearch as PersonSearchIcon,
    ContentCopy as ContentCopyIcon,
    PhotoCamera as PhotoCameraIcon,
    Close as CloseIcon
} from '@mui/icons-material';
import dayjs from 'dayjs';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { initializeDB } from '../../../config/db';
import { JOB_STORE } from '../../../config/constants';
import { generateUUID } from '../../../utils/uuid';
import Swal from 'sweetalert2';
import packingSlipService from '../../../services/packingSlipService';
import SignatureModalWithAuto from '../../common/SignatureModalWithAuto';
import SelectEmployeesAndTempsModal from '../../jha/SelectEmployeesAndTempsModal';
import CameraCapture from '../../shared/CameraCapture';

const PHOTO_MAX_DIMENSION = 1280;
const PHOTO_JPEG_QUALITY = 0.75;

async function compressPhotoDataUrl(dataUrl, maxDimension = PHOTO_MAX_DIMENSION, quality = PHOTO_JPEG_QUALITY) {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => {
            let { width, height } = img;
            if (width > maxDimension || height > maxDimension) {
                if (width >= height) {
                    height = Math.round((height * maxDimension) / width);
                    width = maxDimension;
                } else {
                    width = Math.round((width * maxDimension) / height);
                    height = maxDimension;
                }
            }
            const canvas = document.createElement('canvas');
            canvas.width = width;
            canvas.height = height;
            const ctx = canvas.getContext('2d');
            ctx.drawImage(img, 0, 0, width, height);
            try {
                resolve(canvas.toDataURL('image/jpeg', quality));
            } catch (e) {
                reject(e);
            }
        };
        img.onerror = reject;
        img.src = dataUrl;
    });
}

const STATUS_DRAFT = 'draft';
const STATUS_SUBMITTED = 'submitted';

export default function PackingSlipForm() {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const uuid = searchParams.get('uuid');

    const userFullName = localStorage.getItem('employeeFullName') || '';
    const userId = localStorage.getItem('employeeId') || '';

    const [form, setForm] = useState({
        uuid: uuid || generateUUID(),
        date: dayjs().format('YYYY-MM-DD'),
        shippedTo: '',
        shipDate: dayjs().format('YYYY-MM-DD'),
        po: '',
        jobId: null,
        jobNumber: '',
        jobName: '',
        items: [{ itemNumber: '1', description: '', qty: '', photos: [] }],
        pullerEmployeeId: userId,
        pullerName: userFullName,
        pullerSignature: '',
        pullerSignatureDate: dayjs().format('YYYY-MM-DD'),
        receivedByCustomerName: '',
        receivedBySignature: '',
        receivedByDate: dayjs().format('YYYY-MM-DD'),
        status: STATUS_DRAFT,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
    });

    const [errors, setErrors] = useState({});
    const [submitted, setSubmitted] = useState(false);
    const [saving, setSaving] = useState(false);
    const [loading, setLoading] = useState(true);
    const [jobs, setJobs] = useState([]);
    const [pullerModalOpen, setPullerModalOpen] = useState(false);
    const [pullerSigModalOpen, setPullerSigModalOpen] = useState(false);
    const [receivedBySigModalOpen, setReceivedBySigModalOpen] = useState(false);
    const [cameraOpen, setCameraOpen] = useState(false);
    const [cameraItemIndex, setCameraItemIndex] = useState(null);
    const [photoProcessing, setPhotoProcessing] = useState(false);

    const saveTimer = useRef(null);
    const pullerSigPadRef = useRef(null);
    const receivedBySigPadRef = useRef(null);

    const sortedJobs = useMemo(() => {
        const jobsWithLabels = jobs.map(job => ({
            ...job,
            label: `${job.Job} - ${job.JobName}`
        }));
        return jobsWithLabels.sort((a, b) => {
            const jobA = parseInt(a.Job) || 0;
            const jobB = parseInt(b.Job) || 0;
            return jobB - jobA;
        });
    }, [jobs]);

    useEffect(() => {
        const loadData = async () => {
            if (uuid) {
                const existingForm = await packingSlipService.loadByUuid(uuid);
                if (existingForm) {
                    const normalizedItems = (existingForm.items || []).map(item => ({
                        ...item,
                        photos: Array.isArray(item.photos) ? item.photos : []
                    }));
                    setForm({ ...existingForm, items: normalizedItems });
                    setSubmitted(existingForm.status === STATUS_SUBMITTED);
                }
            }

            const db = await initializeDB();
            const allJobs = await db.getAll(JOB_STORE);
            setJobs(allJobs.sort((a, b) => (a.jobNumber || '').localeCompare(b.jobNumber || '')));

            setLoading(false);
        };
        loadData();
    }, [uuid]);

    const handleFieldChange = (field, value) => {
        const updated = { ...form, [field]: value };
        setForm(updated);
        queueSave(updated);
    };

    const handleItemChange = (index, field, value) => {
        const newItems = [...form.items];
        newItems[index][field] = value;
        const updated = { ...form, items: newItems };
        setForm(updated);
        queueSave(updated);
    };

    const addItem = () => {
        const lastItemNumber = form.items.length > 0
            ? parseInt(form.items[form.items.length - 1].itemNumber) || form.items.length
            : 0;
        const updated = {
            ...form,
            items: [...form.items, { itemNumber: String(lastItemNumber + 1), description: '', qty: '', photos: [] }]
        };
        setForm(updated);
        queueSave(updated);
    };

    const removeItem = (index) => {
        if (form.items.length === 1) return;
        const newItems = form.items.filter((_, i) => i !== index);
        const updated = { ...form, items: newItems };
        setForm(updated);
        queueSave(updated);
    };

    const duplicateItem = (index) => {
        const itemToDuplicate = form.items[index];
        const lastItemNumber = form.items.length > 0
            ? parseInt(form.items[form.items.length - 1].itemNumber) || form.items.length
            : 0;
        const duplicated = {
            ...itemToDuplicate,
            itemNumber: String(lastItemNumber + 1),
            photos: []
        };
        const updated = {
            ...form,
            items: [...form.items, duplicated]
        };
        setForm(updated);
        queueSave(updated);
    };

    const openCameraForItem = (index) => {
        setCameraItemIndex(index);
        setCameraOpen(true);
    };

    const handlePhotoCapture = async (photoData) => {
        if (cameraItemIndex === null || !photoData?.dataUrl) return;
        setPhotoProcessing(true);
        try {
            const compressed = await compressPhotoDataUrl(photoData.dataUrl);
            const newPhoto = {
                id: null,
                dataUrl: compressed,
                name: photoData.name || `photo_${Date.now()}.jpg`,
                timestamp: photoData.timestamp || new Date().toISOString()
            };
            const newItems = [...form.items];
            const targetItem = newItems[cameraItemIndex];
            if (!targetItem) return;
            newItems[cameraItemIndex] = {
                ...targetItem,
                photos: [...(targetItem.photos || []), newPhoto]
            };
            const updated = { ...form, items: newItems };
            setForm(updated);
            queueSave(updated);
        } catch (e) {
            console.error('Failed to process photo:', e);
            Swal.fire('Error', 'Failed to process photo', 'error');
        } finally {
            setPhotoProcessing(false);
        }
    };

    const removePhoto = (itemIndex, photoIndex) => {
        const newItems = [...form.items];
        const targetItem = newItems[itemIndex];
        if (!targetItem) return;
        const newPhotos = [...(targetItem.photos || [])];
        newPhotos.splice(photoIndex, 1);
        newItems[itemIndex] = { ...targetItem, photos: newPhotos };
        const updated = { ...form, items: newItems };
        setForm(updated);
        queueSave(updated);
    };

    const queueSave = (formData) => {
        if (saveTimer.current) clearTimeout(saveTimer.current);
        saveTimer.current = setTimeout(() => saveDraft(formData || form), 1000);
    };

    const saveDraft = async (formData) => {
        try {
            const dataToSave = {
                ...(formData || form),
                updatedAt: new Date().toISOString()
            };
            await packingSlipService.save(dataToSave);
        } catch (error) {
            console.error('Error saving draft:', error);
        }
    };

    const handleJobChange = (event, newValue) => {
        if (!newValue) {
            const updated = {
                ...form,
                jobId: null,
                jobNumber: '',
                jobName: '',
                shippedTo: ''
            };
            setForm(updated);
            queueSave(updated);
            return;
        }

        const updated = {
            ...form,
            jobId: newValue.id,
            jobNumber: newValue.Job || '',
            jobName: newValue.JobName || '',
            shippedTo: newValue.Customer || ''
        };
        setForm(updated);
        queueSave(updated);
    };

    const handleSelectPuller = (employees) => {
        if (employees.length > 0) {
            const emp = employees[0];
            const updated = {
                ...form,
                pullerEmployeeId: emp.employeeId || emp.id,
                pullerName: emp.fullName || emp.employeeName
            };
            setForm(updated);
            queueSave(updated);
        }
        setPullerModalOpen(false);
    };

    const handlePullerSignatureAccept = () => {
        if (!pullerSigPadRef.current) {
            console.error('Signature pad ref not found');
            return;
        }

        const dataUrl = pullerSigPadRef.current.toDataURL();
        const updated = {
            ...form,
            pullerSignature: dataUrl,
            pullerSignatureDate: dayjs().format('YYYY-MM-DD')
        };
        setForm(updated);
        queueSave(updated);
        setPullerSigModalOpen(false);
    };

    const handleReceivedBySignatureAccept = () => {
        if (!receivedBySigPadRef.current) {
            console.error('Signature pad ref not found');
            return;
        }

        const dataUrl = receivedBySigPadRef.current.toDataURL();
        const updated = {
            ...form,
            receivedBySignature: dataUrl,
            receivedByDate: dayjs().format('YYYY-MM-DD')
        };
        setForm(updated);
        queueSave(updated);
        setReceivedBySigModalOpen(false);
    };

    const handleClearPullerSignature = () => {
        const updated = {
            ...form,
            pullerSignature: ''
        };
        setForm(updated);
        queueSave(updated);
    };

    const handleClearReceivedBySignature = () => {
        const updated = {
            ...form,
            receivedBySignature: ''
        };
        setForm(updated);
        queueSave(updated);
    };

    const validateForm = () => {
        const newErrors = {};
        if (!form.shipDate) newErrors.shipDate = 'Ship Date is required';
        if (!form.jobNumber) newErrors.jobNumber = 'Job is required';
        if (!form.items || form.items.length === 0) newErrors.items = 'At least one item is required';
        if (!form.pullerSignature) newErrors.pullerSignature = 'Puller signature is required';

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async () => {
        if (!validateForm()) {
            Swal.fire('Error', 'Please fill in all required fields', 'error');
            return;
        }

        const result = await Swal.fire({
            title: 'Submit Packing Slip?',
            text: 'Once submitted, you cannot edit this packing slip.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Yes, submit it!'
        });

        if (result.isConfirmed) {
            setSaving(true);
            try {
                const submittedForm = {
                    ...form,
                    status: STATUS_SUBMITTED,
                    submittedAt: new Date().toISOString(),
                    submittedBy: userId,
                    submittedByName: userFullName
                };

                const response = await packingSlipService.submit(submittedForm);

                if (response.success) {
                    await packingSlipService.deleteFromIndexedDB(form.uuid);
                    Swal.fire('Success', 'Packing slip submitted successfully!', 'success');
                    navigate('/electrical/packing-slip/list');
                } else {
                    throw new Error(response.message || 'Submission failed');
                }
            } catch (e) {
                console.error('Error submitting:', e);
                Swal.fire('Error', 'Failed to submit packing slip: ' + (e.message || 'Unknown error'), 'error');
            } finally {
                setSaving(false);
            }
        }
    };

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box sx={{
            height: 'calc(100vh - 60px)',
            backgroundColor: '#f5f5f5',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden'
        }}>
            {/* Header */}
            <Box sx={{ backgroundColor: 'white', borderBottom: '1px solid #e0e0e0', p: 1.5, flexShrink: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <IconButton onClick={() => navigate('/electrical/packing-slip/list')} edge="start">
                            <ArrowBackIcon />
                        </IconButton>
                        <Typography variant="h6" fontWeight={600}>PACKING SLIP</Typography>
                    </Box>
                    <Chip
                        label={submitted ? 'Submitted' : 'Draft'}
                        size="small"
                        sx={{
                            backgroundColor: submitted ? '#4caf50' : '#9e9e9e',
                            color: 'white'
                        }}
                    />
                </Box>
            </Box>

            {/* Form Content */}
            <Box sx={{ flex: 1, overflow: 'auto' }}>
                <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
                    {/* Basic Information */}
                    <Accordion defaultExpanded sx={{
                        mb: 0.5,
                        '&.Mui-expanded': {
                            margin: '0 0 4px 0'
                        }
                    }}>
                        <AccordionSummary
                            expandIcon={<ExpandMoreIcon />}
                            sx={{
                                minHeight: '40px !important',
                                '& .MuiAccordionSummary-content': {
                                    margin: '6px 0 !important'
                                }
                            }}
                        >
                            <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.875rem' }}>Basic Information</Typography>
                        </AccordionSummary>
                        <AccordionDetails sx={{ p: 1.5, pt: 1 }}>
                            <Grid container rowSpacing={1} columnSpacing={1}>
                                <Grid item xs={12} sm={4}>
                                    <Autocomplete
                                        options={sortedJobs}
                                        getOptionLabel={(option) => option.label || ''}
                                        value={sortedJobs.find(j => j.Job === form.jobNumber) || null}
                                        onChange={handleJobChange}
                                        disabled={submitted}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                size="small"
                                                label="JOB *"
                                                error={!!errors.jobNumber}
                                                helperText={errors.jobNumber}
                                            />
                                        )}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        label="DATE *"
                                        type="date"
                                        value={form.shipDate}
                                        onChange={(e) => handleFieldChange('shipDate', e.target.value)}
                                        disabled={submitted}
                                        error={!!errors.shipDate}
                                        helperText={errors.shipDate}
                                        InputLabelProps={{ shrink: true }}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        label="PO"
                                        value={form.po}
                                        onChange={(e) => handleFieldChange('po', e.target.value)}
                                        disabled={submitted}
                                    />
                                </Grid>
                            </Grid>
                        </AccordionDetails>
                    </Accordion>

                    {/* Items */}
                    <Card elevation={1} sx={{ mb: 0.5 }}>
                        <CardContent sx={{ py: 1.75 }}>
                            <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.25}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Items</Typography>
                            </Box>
                            {form.items.length === 0 ? (
                                <Typography variant="body2" color="text.secondary" textAlign="center" py={2}>
                                    No items added yet. Click "Add Item" to get started.
                                </Typography>
                            ) : (
                                <Stack spacing={1.25}>
                                    {form.items.map((item, index) => (
                                        <Card key={index} variant="outlined" sx={{ bgcolor: '#fafafa' }}>
                                            <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                                                <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                                                    <Typography variant="subtitle2" fontWeight={600}>
                                                        Item
                                                    </Typography>
                                                    {!submitted && (
                                                        <Box display="flex" gap={0.5}>
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => duplicateItem(index)}
                                                                color="info"
                                                            >
                                                                <ContentCopyIcon fontSize="small" />
                                                            </IconButton>
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => removeItem(index)}
                                                                color="error"
                                                                disabled={form.items.length === 1}
                                                            >
                                                                <DeleteIcon fontSize="small" />
                                                            </IconButton>
                                                        </Box>
                                                    )}
                                                </Box>
                                                <Grid container rowSpacing={1} columnSpacing={{ xs: 1, md: 1.5 }}>
                                                    <Grid item xs={12} sm={2}>
                                                        <TextField
                                                            fullWidth
                                                            size="small"
                                                            label="Item #"
                                                            value={item.itemNumber}
                                                            onChange={(e) => handleItemChange(index, 'itemNumber', e.target.value)}
                                                            disabled={submitted}
                                                        />
                                                    </Grid>
                                                    <Grid item xs={12} sm={7}>
                                                        <TextField
                                                            fullWidth
                                                            size="small"
                                                            label="Description"
                                                            value={item.description}
                                                            onChange={(e) => handleItemChange(index, 'description', e.target.value)}
                                                            disabled={submitted}
                                                        />
                                                    </Grid>
                                                    <Grid item xs={12} sm={3}>
                                                        <TextField
                                                            fullWidth
                                                            size="small"
                                                            label="QTY"
                                                            type="number"
                                                            value={item.qty}
                                                            onChange={(e) => handleItemChange(index, 'qty', e.target.value)}
                                                            disabled={submitted}
                                                        />
                                                    </Grid>
                                                    <Grid item xs={12}>
                                                        <Box display="flex" alignItems="center" justifyContent="space-between" mb={0.75}>
                                                            <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                                                                Photos {item.photos?.length > 0 ? `(${item.photos.length})` : ''}
                                                            </Typography>
                                                            {!submitted && (
                                                                <Button
                                                                    size="small"
                                                                    variant="outlined"
                                                                    startIcon={<PhotoCameraIcon fontSize="small" />}
                                                                    onClick={() => openCameraForItem(index)}
                                                                    disabled={photoProcessing}
                                                                >
                                                                    Add Photo
                                                                </Button>
                                                            )}
                                                        </Box>
                                                        {item.photos?.length > 0 ? (
                                                            <Box display="flex" flexWrap="wrap" gap={1}>
                                                                {item.photos.map((photo, photoIndex) => {
                                                                    const src = photo.dataUrl || photo.webPath || '';
                                                                    return (
                                                                        <Box
                                                                            key={photoIndex}
                                                                            position="relative"
                                                                            sx={{
                                                                                width: 96,
                                                                                height: 96,
                                                                                borderRadius: 1,
                                                                                overflow: 'hidden',
                                                                                border: '1px solid #ddd',
                                                                                bgcolor: '#fff'
                                                                            }}
                                                                        >
                                                                            <img
                                                                                src={src}
                                                                                alt={photo.name || `Photo ${photoIndex + 1}`}
                                                                                style={{
                                                                                    width: '100%',
                                                                                    height: '100%',
                                                                                    objectFit: 'cover'
                                                                                }}
                                                                            />
                                                                            {!submitted && (
                                                                                <IconButton
                                                                                    size="small"
                                                                                    onClick={() => removePhoto(index, photoIndex)}
                                                                                    sx={{
                                                                                        position: 'absolute',
                                                                                        top: 2,
                                                                                        right: 2,
                                                                                        bgcolor: 'rgba(0,0,0,0.6)',
                                                                                        color: 'white',
                                                                                        width: 22,
                                                                                        height: 22,
                                                                                        '&:hover': { bgcolor: 'rgba(0,0,0,0.85)' }
                                                                                    }}
                                                                                >
                                                                                    <CloseIcon sx={{ fontSize: 14 }} />
                                                                                </IconButton>
                                                                            )}
                                                                        </Box>
                                                                    );
                                                                })}
                                                            </Box>
                                                        ) : (
                                                            <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                                                No photos attached
                                                            </Typography>
                                                        )}
                                                    </Grid>
                                                </Grid>
                                            </CardContent>
                                        </Card>
                                    ))}
                                    {!submitted && form.items.length > 0 && (
                                        <Box display="flex" justifyContent="flex-end" mt={1.5}>
                                            <Button
                                                variant="contained"
                                                startIcon={<AddIcon />}
                                                onClick={addItem}
                                                size="small"
                                            >
                                                Add Item
                                            </Button>
                                        </Box>
                                    )}
                                </Stack>
                            )}
                        </CardContent>
                    </Card>

                    {/* Signatures */}
                    <Card elevation={1} sx={{ mb: 0.5 }}>
                        <CardContent sx={{ py: 1.75 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2 }}>Signatures</Typography>

                            {/* Pulled/Shipped By */}
                            <Box mb={2}>
                                <Typography variant="body2" sx={{ fontWeight: 500, mb: 1 }}>Pulled/Shipped By</Typography>
                                <Grid container rowSpacing={1} columnSpacing={{ xs: 1, md: 1.5 }} alignItems="center">
                                    <Grid item xs={12} sm={4}>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            label="Puller *"
                                            value={form.pullerName}
                                            onClick={() => {
                                                if (!submitted) {
                                                    setPullerModalOpen(true);
                                                }
                                            }}
                                            InputProps={{
                                                readOnly: true,
                                                style: { cursor: submitted ? 'default' : 'pointer' },
                                                endAdornment: !submitted && (
                                                    <InputAdornment position="end">
                                                        <IconButton onClick={() => setPullerModalOpen(true)} size="small">
                                                            <PersonSearchIcon fontSize="small" />
                                                        </IconButton>
                                                    </InputAdornment>
                                                )
                                            }}
                                            disabled={submitted}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={4}>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            label="Date"
                                            type="date"
                                            value={form.pullerSignatureDate}
                                            onChange={(e) => handleFieldChange('pullerSignatureDate', e.target.value)}
                                            disabled={submitted}
                                            InputLabelProps={{ shrink: true }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={4}>
                                        <Box display="flex" justifyContent="center" alignItems="center">
                                            {form.pullerSignature ? (
                                                <Box position="relative" display="inline-block">
                                                    <img
                                                        src={form.pullerSignature}
                                                        alt="Signature"
                                                        style={{
                                                            maxWidth: 200,
                                                            height: 60,
                                                            border: '1px solid #ddd',
                                                            borderRadius: 4,
                                                            cursor: submitted ? 'default' : 'pointer'
                                                        }}
                                                        onClick={() => {
                                                            if (!submitted) {
                                                                setPullerSigModalOpen(true);
                                                            }
                                                        }}
                                                    />
                                                    {!submitted && (
                                                        <IconButton
                                                            size="small"
                                                            onClick={handleClearPullerSignature}
                                                            sx={{ position: 'absolute', top: -8, right: -8, bgcolor: 'error.main', color: 'white', '&:hover': { bgcolor: 'error.dark' } }}
                                                        >
                                                            <ClearIcon sx={{ fontSize: 16 }} />
                                                        </IconButton>
                                                    )}
                                                </Box>
                                            ) : (
                                                <Button
                                                    variant="outlined"
                                                    size="small"
                                                    startIcon={<DrawIcon />}
                                                    onClick={() => setPullerSigModalOpen(true)}
                                                    disabled={submitted}
                                                >
                                                    Add Signature
                                                </Button>
                                            )}
                                        </Box>
                                        {errors.pullerSignature && (
                                            <Typography color="error" variant="caption" display="block" mt={0.5} textAlign="center">
                                                {errors.pullerSignature}
                                            </Typography>
                                        )}
                                    </Grid>
                                </Grid>
                            </Box>

                            {/* Received By */}
                            <Box>
                                <Typography variant="body2" sx={{ fontWeight: 500, mb: 1 }}>Received By (Customer)</Typography>
                                <Grid container rowSpacing={1} columnSpacing={{ xs: 1, md: 1.5 }} alignItems="center">
                                    <Grid item xs={12} sm={4}>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            label="Customer Name"
                                            value={form.receivedByCustomerName}
                                            onChange={(e) => handleFieldChange('receivedByCustomerName', e.target.value)}
                                            disabled={submitted}
                                            error={!!errors.receivedByCustomerName}
                                            helperText={errors.receivedByCustomerName}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={4}>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            label="Date"
                                            type="date"
                                            value={form.receivedByDate}
                                            onChange={(e) => handleFieldChange('receivedByDate', e.target.value)}
                                            disabled={submitted}
                                            InputLabelProps={{ shrink: true }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={4}>
                                        <Box display="flex" justifyContent="center" alignItems="center">
                                            {form.receivedBySignature ? (
                                                <Box position="relative" display="inline-block">
                                                    <img
                                                        src={form.receivedBySignature}
                                                        alt="Signature"
                                                        style={{
                                                            maxWidth: 200,
                                                            height: 60,
                                                            border: '1px solid #ddd',
                                                            borderRadius: 4,
                                                            cursor: submitted ? 'default' : 'pointer'
                                                        }}
                                                        onClick={() => {
                                                            if (!submitted) {
                                                                setReceivedBySigModalOpen(true);
                                                            }
                                                        }}
                                                    />
                                                    {!submitted && (
                                                        <IconButton
                                                            size="small"
                                                            onClick={handleClearReceivedBySignature}
                                                            sx={{ position: 'absolute', top: -8, right: -8, bgcolor: 'error.main', color: 'white', '&:hover': { bgcolor: 'error.dark' } }}
                                                        >
                                                            <ClearIcon sx={{ fontSize: 16 }} />
                                                        </IconButton>
                                                    )}
                                                </Box>
                                            ) : (
                                                <Button
                                                    variant="outlined"
                                                    size="small"
                                                    startIcon={<DrawIcon />}
                                                    onClick={() => setReceivedBySigModalOpen(true)}
                                                    disabled={submitted}
                                                >
                                                    Add Signature
                                                </Button>
                                            )}
                                        </Box>
                                        {errors.receivedBySignature && (
                                            <Typography color="error" variant="caption" display="block" mt={0.5} textAlign="center">
                                                {errors.receivedBySignature}
                                            </Typography>
                                        )}
                                    </Grid>
                                </Grid>
                            </Box>
                        </CardContent>
                    </Card>

                    {/* Submit Button */}
                    {!submitted && (
                        <Box display="flex" justifyContent="center" sx={{ my: 2 }}>
                            <Button
                                variant="contained"
                                color="primary"
                                size="large"
                                onClick={handleSubmit}
                                disabled={saving}
                                startIcon={saving ? <CircularProgress size={20} /> : <SendIcon />}
                                sx={{ minWidth: 200 }}
                            >
                                {saving ? 'Submitting...' : 'Submit Form'}
                            </Button>
                        </Box>
                    )}
                </Box>
            </Box>

            {/* Modals */}
            <SelectEmployeesAndTempsModal
                open={pullerModalOpen}
                onClose={() => setPullerModalOpen(false)}
                onSelect={handleSelectPuller}
                singleSelect={true}
                alreadySelected={[]}
            />

            <SignatureModalWithAuto
                open={pullerSigModalOpen}
                onClose={() => setPullerSigModalOpen(false)}
                onAccept={handlePullerSignatureAccept}
                sigPadRef={pullerSigPadRef}
                employeeName={form.pullerName}
                existingSignature={form.pullerSignature}
                title="Puller/Shipped By Signature"
            />

            <SignatureModalWithAuto
                open={receivedBySigModalOpen}
                onClose={() => setReceivedBySigModalOpen(false)}
                onAccept={handleReceivedBySignatureAccept}
                sigPadRef={receivedBySigPadRef}
                employeeName={form.receivedByCustomerName}
                existingSignature={form.receivedBySignature}
                title="Received By (Customer) Signature"
            />

            <CameraCapture
                open={cameraOpen}
                onClose={() => {
                    setCameraOpen(false);
                    setCameraItemIndex(null);
                }}
                onCapture={handlePhotoCapture}
            />
        </Box>
    );
}
